import { Fragment } from '@tiptap/pm/model';
import { EditorState, TextSelection, Transaction } from '@tiptap/pm/state';
import { TableMap } from '@tiptap/pm/tables';

type MergeDirection = 'above' | 'below';

interface FocusedTableContext {
  table: ReturnType<EditorState['doc']['nodeAt']>;
  tablePos: number;
  tableIndex: number;
  parent: ReturnType<EditorState['selection']['$anchor']['node']>;
  depth: number;
}

interface MergeContext {
  focusedTable: NonNullable<FocusedTableContext['table']>;
  focusedTablePos: number;
  neighborTable: NonNullable<FocusedTableContext['table']>;
  neighborTablePos: number;
  direction: MergeDirection;
}

/**
 * Returns whether the focused table can be merged with the direct table above it.
 */
export function canMergeTableWithAbove(state: EditorState) {
  return getTableMergeContext(state, 'above') !== null;
}

/**
 * Returns whether the focused table can be merged with the direct table below it.
 */
export function canMergeTableWithBelow(state: EditorState) {
  return getTableMergeContext(state, 'below') !== null;
}

/**
 * Merges the focused table with the direct table above it.
 */
export function mergeTableWithAbove(state: EditorState, dispatch?: (tr: Transaction) => void) {
  return mergeTable(state, 'above', dispatch);
}

/**
 * Merges the focused table with the direct table below it.
 */
export function mergeTableWithBelow(state: EditorState, dispatch?: (tr: Transaction) => void) {
  return mergeTable(state, 'below', dispatch);
}

/**
 * Merges the focused table with the neighboring table in the requested direction.
 */
function mergeTable(state: EditorState, direction: MergeDirection, dispatch?: (tr: Transaction) => void) {
  const context = getTableMergeContext(state, direction);

  if (!context) {
    return false;
  }

  if (dispatch) {
    const tr = state.tr;
    applyTableMerge(tr, context);
    dispatch(tr);
  }

  return true;
}

/**
 * Returns the focused table and its direct compatible neighbor for the requested direction.
 */
function getTableMergeContext(state: EditorState, direction: MergeDirection): MergeContext | null {
  const focusedTableContext = getFocusedTableContext(state);

  if (!focusedTableContext?.table) {
    return null;
  }

  const { table, tablePos, tableIndex, parent } = focusedTableContext;
  const neighborIndex = direction === 'above' ? tableIndex - 1 : tableIndex + 1;

  if (neighborIndex < 0 || neighborIndex >= parent.childCount) {
    return null;
  }

  const neighborTable = parent.child(neighborIndex);

  if (neighborTable.type.name !== 'table') {
    return null;
  }

  if (TableMap.get(table).width !== TableMap.get(neighborTable).width) {
    return null;
  }

  const neighborTablePos = direction === 'above' ? tablePos - neighborTable.nodeSize : tablePos + table.nodeSize;

  return {
    focusedTable: table,
    focusedTablePos: tablePos,
    neighborTable,
    neighborTablePos,
    direction,
  };
}

/**
 * Returns the currently focused table context from the selection anchor.
 */
function getFocusedTableContext(state: EditorState): FocusedTableContext | null {
  const { $anchor } = state.selection;

  for (let depth = $anchor.depth; depth > 0; depth -= 1) {
    const node = $anchor.node(depth);

    if (node.type.name !== 'table') {
      continue;
    }

    return {
      table: node,
      tablePos: $anchor.before(depth),
      tableIndex: $anchor.index(depth - 1),
      parent: $anchor.node(depth - 1),
      depth,
    };
  }

  return null;
}

/**
 * Applies the requested table merge transaction and keeps selection in the focused table portion.
 */
function applyTableMerge(
  tr: Transaction,
  { focusedTable, focusedTablePos, neighborTable, neighborTablePos, direction }: MergeContext
) {
  const rowsBeforeFocused = direction === 'above' ? getTableRows(neighborTable) : [];
  const focusedRows = getTableRows(focusedTable);
  const rowsAfterFocused = direction === 'below' ? getTableRows(neighborTable) : [];
  const mergedRows = [...rowsBeforeFocused, ...focusedRows, ...rowsAfterFocused];
  const mergedTable = focusedTable.type.createChecked(
    focusedTable.attrs,
    Fragment.fromArray(mergedRows),
    focusedTable.marks
  );
  const replaceFrom = direction === 'above' ? neighborTablePos : focusedTablePos;
  const replaceTo =
    direction === 'above' ? focusedTablePos + focusedTable.nodeSize : neighborTablePos + neighborTable.nodeSize;

  tr.replaceWith(replaceFrom, replaceTo, mergedTable);

  const focusedRowOffset = rowsBeforeFocused.reduce((sum, row) => sum + row.nodeSize, 0);
  tr.setSelection(TextSelection.near(tr.doc.resolve(replaceFrom + 1 + focusedRowOffset)));
  tr.scrollIntoView();
}

/**
 * Returns all rows of the provided table node.
 */
function getTableRows(table: NonNullable<MergeContext['focusedTable']>) {
  return Array.from({ length: table.childCount }, (_, index) => table.child(index));
}
